import React, { useState } from 'react';
import isEmpty from 'lodash/isEmpty';
import styled from 'styled-components';
import { AlertHeader, Button, DOBInput, FieldWrapper, ModalContent, ModalFooter, ModalForm, ModalFormLayout, ModalHeader, ModalLayout, ModalText, Text } from 'components';
import { appConfig } from 'config/app';
import { theme, toRem } from 'design';
import { AuthService, validation } from 'lib';
import { IEmployerState } from 'store/types';

const StyledLink = styled(Text)`
  margin-top: ${toRem(27.5)};
`;

const Message = styled(ModalText)`
  margin-bottom: ${toRem(27.5)};
`;

const ErrorMessage = styled(ModalText)`
  color: ${theme.color.danger.medium};
  margin-top: ${toRem(27.5)};
  padding: 0 ${toRem(25)};
`;

const Employer = styled.p`
  font-size: ${toRem(13)};
  padding: ${toRem(5)} 0;
  word-break: break-word;
`;

const EmployersList = styled.div`
  display: block;
  padding: ${toRem(5)} 0;
`;

const EmployerTitle = styled.div`
  font-size: ${toRem(14)};
  font-weight: 700;
  padding-bottom: ${toRem(5)};
`;

const ScrollableModalFormLayout = styled(ModalFormLayout)`
  justify-items: left;
  max-height: 35vh;
  overflow-y: auto;
`;

interface DeleteAccountProps {
  onDelete?: (dob: string) => void;
  onClose: () => void;
  organizations: Record<string, IEmployerState>;
  isLoading: boolean;
  error?: boolean;
}

const DeleteAccount = ({ onClose, onDelete, organizations, isLoading, error }: DeleteAccountProps) => {
  const [dob, setDOB] = useState('');
  const [dobError, setDobError] = useState<string>('');
  const [showError, setShowError] = useState<boolean>(false);

  const enableSubmit = dob !== '';

  const isDismissed = isEmpty(organizations);

  const orgsList = isDismissed ? [] : Object.values(organizations).filter((v) => v.type === 'admin');
  const sitesList = isDismissed ? [] : Object.values(organizations).filter((v) => v.type === 'user');

  const isSalesforceSupport = process.env.REACT_APP_SALESFORCE_SUPPORT;
  const isSalesforceSSOBaseUrl = process.env.REACT_APP_SALESFORCE_SSO_BASE_URL;
  const jwt = AuthService.getToken();

  const onDOBChange = async (e: any) => {
    setDOB(e.target.value);
    validation.DOBValidationSchema.dateOfBirth
      .validate(e.target.value)
      .then(() => setDobError(''))
      .catch((err) => setDobError(err.errors[0]));
  };

  const onDOBBlur = () => {
    if (!showError) setShowError(true);
  };

  return (
    <ModalLayout isActive onClose={onClose}>
      <ModalHeader>
        <AlertHeader icon="Caution" iconFill={theme.color.warning.medium} title={isDismissed ? 'Delete account?' : 'Unable to delete account'} />
      </ModalHeader>

      <ModalContent>
        {isDismissed ? (
          <Message>Enter your birthdate below to confirm account deletion. You will no longer be able to send or receive payments, and this action cannot be undone.</Message>
        ) : (
          <Message>You must be dismissed from each of your current employers to delete your account. Please contact the following employers and ask to be dismissed.</Message>
        )}

        <ModalForm>
          {isDismissed ? (
            <ModalFormLayout>
              <FieldWrapper label="Date of Birth" data-cy="date-of-birth" message={showError ? dobError : ''} hasError={!!dobError}>
                <DOBInput placeholder="MM-DD-YYYY" value={dob} onChange={onDOBChange} onBlur={onDOBBlur} />
              </FieldWrapper>
            </ModalFormLayout>
          ) : (
            <ScrollableModalFormLayout data-cy="employers" compact>
              {!!orgsList.length && (
                <EmployersList>
                  <EmployerTitle>Organizations</EmployerTitle>
                  {orgsList.map((v, k) => (
                    <Employer key={k}>{v.name}</Employer>
                  ))}
                </EmployersList>
              )}
              {!!sitesList.length && (
                <EmployersList>
                  <EmployerTitle>Sites</EmployerTitle>
                  {sitesList.map((v, k) => (
                    <Employer key={k}>{v.name}</Employer>
                  ))}
                </EmployersList>
              )}
            </ScrollableModalFormLayout>
          )}

          {!isDismissed && (
            <>
              {isSalesforceSupport && isSalesforceSSOBaseUrl && jwt ? (
                <StyledLink as="a" href={`${isSalesforceSSOBaseUrl}?jwt=${jwt}`} rel="noopener noreferrer" size="small" target="_blank">
                  Contact Support
                </StyledLink>
              ) : (
                <StyledLink as="a" href={appConfig.LINKS.SUPPORT} rel="noopener noreferrer" size="small" target="_blank">
                  Contact Support
                </StyledLink>
              )}
            </>
          )}

          {error && <ErrorMessage data-cy="error-message">The date of birth you entered does not match our records. Please try again.</ErrorMessage>}

          <ModalFooter columns={isDismissed ? 2 : 1}>
            <Button appearance="white" size="large" label="Cancel" onClick={onClose} />
            {isDismissed && <Button appearance="destructive" size="large" label="Delete" disabled={!enableSubmit} onClick={() => onDelete && onDelete(dob)} isProcessing={isLoading} />}
          </ModalFooter>
        </ModalForm>
      </ModalContent>
    </ModalLayout>
  );
};

export default DeleteAccount;
